import React, { useState } from "react";
import { format, startOfMonth } from "date-fns";
import { Loader2 } from "lucide-react";
import { DateRange } from "react-day-picker";
import { capitalize, camelCase } from "lodash";

import { Dialog, Checkbox, Button, Progress, Field } from "@/components/ui";
import { LeaderboardMetric, StatsServices, LeaderboardResult } from "@/services/bedrock";
import { useSocialCoachSession, URLS } from "@/utils";
import { exportToXlsx, getAutoWidths } from "@/utils/xlsxExportHelpers";
import { REPORT_GROUPS, ALL_REPORT_OPTIONS, LEADERBOARD_HEADER_MAP, ReportGroup } from "./reports";
import { XlsxSheet } from "@/interfaces";
import { ReportDateRange } from "@/components/ReportDateRange";

const formatHeaderFallback = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BulkLeaderboardExportDialog({ open, onOpenChange }: Props) {
  const { coachId } = useSocialCoachSession();
  const [date, setDate] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const [preset, setPreset] = useState<string>("this_month");
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>(["player_perf", "posts"]);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const isAllSelected = ALL_REPORT_OPTIONS.every(opt => selectedOptionIds.includes(opt.id));

  const handleGroupToggle = (group: ReportGroup, checked: boolean) => {
    const groupOptionIds = group.options.map(opt => opt.id);
    if (checked) {
      setSelectedOptionIds(prev => Array.from(new Set([...prev, ...groupOptionIds])));
    } else {
      setSelectedOptionIds(prev => prev.filter(id => !groupOptionIds.includes(id)));
    }
  };

  const isGroupSelected = (group: ReportGroup) => {
    return group.options.every(opt => selectedOptionIds.includes(opt.id));
  };

  const handleOptionToggle = (optionId: string) => {
    setSelectedOptionIds(prev => (prev.includes(optionId) ? prev.filter(id => id !== optionId) : [...prev, optionId]));
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOptionIds(ALL_REPORT_OPTIONS.map(opt => opt.id));
    } else {
      setSelectedOptionIds([]);
    }
  };

  const formatLeaderboardData = (data: LeaderboardResult[], metric: LeaderboardMetric) => {
    return data.map(item => {
      const id = item.id;
      const origin = typeof window !== "undefined" ? window.location.origin : "";

      const link =
        item.type === "player"
          ? `${origin}${URLS.coach.editPlayer.replace(":playerId", id.toString())}`
          : item.type === "prompt"
          ? `${origin}${URLS.coach.prompt.replace(":promptId", id.toString())}`
          : item.type === "campaign"
          ? `${origin}${URLS.coach.editCampaigns.replace(":campaignId", id.toString())}`
          : item.type === "video_script"
          ? `${origin}${URLS.videoScript.replace(":id", id.toString())}`
          : undefined;

      const rawData: any = { ...item };

      if (item.score !== undefined) {
        rawData[camelCase(metric)] = item.score;
      }

      if (link?.length) {
        const typeTitle = item.type
          .split("_")
          .map(t => capitalize(t))
          .join(" ");
        rawData[`${typeTitle}Link`] = link;
      }

      if ("playerId" in rawData) {
        const playerId = rawData["playerId"].toString();
        rawData["playerLink"] = `${origin}${URLS.coach.editPlayer.replace(":playerId", playerId)}`;
        delete rawData["playerId"];
      }

      delete rawData["id"];
      delete rawData["score"];
      delete rawData["type"];

      const presentationData: Record<string, any> = {};

      for (const key in rawData) {
        if (Object.prototype.hasOwnProperty.call(rawData, key)) {
          let value = rawData[key];
          if (value === null) value = "";
          if (["postedTimestamp", "createdDate"].includes(key) && value) {
            value = new Date(value).toLocaleString();
          }
          const friendlyHeader = LEADERBOARD_HEADER_MAP[key] || formatHeaderFallback(key);
          presentationData[friendlyHeader] = value;
        }
      }

      return presentationData;
    });
  };

  const handleExport = async () => {
    if (!coachId) return;
    if (preset !== "all_time" && !date?.from) return;

    setIsExporting(true);
    setProgress(0);
    const sheets: XlsxSheet[] = [];
    const selectedOptions = ALL_REPORT_OPTIONS.filter(opt => selectedOptionIds.includes(opt.id));

    const dateRangeString =
      preset === "all_time"
        ? "Timeframe: All Time"
        : `Timeframe: ${format(date!.from!, "LLL dd, y")} - ${format(date?.to || new Date(), "LLL dd, y")}`;

    const preparedByString = `Prepared by SocialCoach on ${format(new Date(), "PPp")}`;

    try {
      for (let i = 0; i < selectedOptions.length; i++) {
        const option = selectedOptions[i];
        setStatusMessage(`Fetching ${option.label} leaderboard...`);

        const allResults: LeaderboardResult[] = [];
        const perPage = 100;
        let page = 1;
        let hasMore = true;
        const selectedMetric = option.metric;

        while (hasMore) {
          const response = await StatsServices.getLeaderboard({
            coachId,
            entityType: option.value,
            metric: selectedMetric,
            startTime: preset === "all_time" ? undefined : date?.from,
            page,
            perPage,
          });

          allResults.push(...response.results);
          hasMore = page < (response.pagination.totalPages || 0);
          page++;

          const entityProgress = (i / selectedOptions.length) * 100;
          const pageProgress = (page / (response.pagination.totalPages || 1)) * (100 / selectedOptions.length);
          setProgress(Math.min(95, entityProgress + pageProgress));
        }

        const sheetName =
          option.label.split(":")[0].trim() + " - " + (option.label.split(":")[1]?.trim() || option.label);

        const formattedData = formatLeaderboardData(allResults, selectedMetric);

        sheets.push({
          name: sheetName.substring(0, 31),
          data: formattedData,
          columns: getAutoWidths(formattedData),
          headerRows: [
            [preparedByString],
            [`Report: ${option.label}`],
            [dateRangeString],
            [`Description: ${option.description}`],
          ],
        });
      }

      setStatusMessage("Finalizing Excel file...");
      setProgress(98);

      const filename = `Leaderboard_Export_${format(new Date(), "yyyy-MM-dd")}`;
      await exportToXlsx(filename, sheets);

      setProgress(100);
      setStatusMessage("Export complete!");
      setTimeout(() => setIsExporting(false), 1000);
    } catch (error) {
      console.error("Export failed", error);
      setStatusMessage("Export failed. Please try again.");
      setTimeout(() => setIsExporting(false), 3000);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size={"xl"}>
      <Dialog.Header>
        <Dialog.Title>Bulk Leaderboard Export</Dialog.Title>
        <Dialog.Description>
          Generate a comprehensive Excel report across multiple categories and timeframes.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="overflow-y-auto max-h-[calc(100vh-20rem)]">
        {isExporting ? (
          <div className="py-10 flex flex-col items-center justify-center space-y-4">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <div className="text-center">
              <p className="font-medium text-lg">{statusMessage}</p>
              <p className="text-muted-foreground">This might take a moment depending on the data volume.</p>
            </div>
            <div className="w-full max-w-md">
              <Progress value={progress} />
              <p className="text-right text-xs mt-1 text-muted-foreground">{Math.round(progress)}%</p>
            </div>
          </div>
        ) : (
          <div className="grid gap-8">
            <ReportDateRange date={date} preset={preset} onDateChange={setDate} onPresetChange={setPreset} />

            <Field className="grid gap-2">
              <div className="flex items-center justify-between">
                <Field.Label>Leaderboard Categories</Field.Label>
                <div className="flex items-center space-x-2">
                  <Checkbox id="select-all-entities" checked={isAllSelected} onCheckedChange={handleSelectAll} />
                  <label htmlFor="select-all-entities" className="text-xs font-medium leading-none cursor-pointer">
                    Select All
                  </label>
                </div>
              </div>
              <Field.Description>
                Choose which leaderboards to include. Each selection will appear as a separate tab in your Excel file.
              </Field.Description>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-2">
                {REPORT_GROUPS.map(group => (
                  <div
                    key={group.title}
                    className="flex flex-col gap-3 p-4 border border-slate-200 rounded-lg bg-slate-50/50"
                  >
                    <div className="flex items-center gap-2 border-b border-slate-200 pb-2 mb-1">
                      <Checkbox
                        id={`group-${group.title}`}
                        checked={isGroupSelected(group)}
                        onCheckedChange={(checked: boolean) => handleGroupToggle(group, checked)}
                      />
                      <h3 className="font-semibold text-sm text-slate-900">{group.title}</h3>
                    </div>
                    <div className="flex flex-col gap-2.5">
                      {group.options.map(option => (
                        <div key={option.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`option-${option.id}`}
                            checked={selectedOptionIds.includes(option.id)}
                            onCheckedChange={() => handleOptionToggle(option.id)}
                          />
                          <label
                            htmlFor={`option-${option.id}`}
                            className="text-[13px] font-medium leading-tight cursor-pointer hover:text-primary transition-colors"
                          >
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Field>
          </div>
        )}
      </Dialog.Content>
      {!isExporting && (
        <Dialog.Footer>
          <Button variant="outline" onClick={() => setSelectedOptionIds([])}>
            Clear Selection
          </Button>
          <Button
            onClick={handleExport}
            disabled={selectedOptionIds.length === 0 || (preset !== "all_time" && !date?.from)}
          >
            Prepare Export
          </Button>
        </Dialog.Footer>
      )}
    </Dialog>
  );
}
